<script setup lang="ts">
import { todayInParis, type ProjectSummary } from '@cockpit/shared';
import { computed, onMounted, ref, shallowRef, watch } from 'vue';
import { api } from '../api';
import { useMembers } from '../composables/useMembers';
import {
  conflictHeads,
  conflictRows,
  conflictTitle,
  isConflictOp,
  type ConflictOp,
} from '../offline/conflicts';
import { useAuthStore } from '../stores/auth';
import { useOfflineStore } from '../stores/offline';

/**
 * Lot AW (maquette, repère 36) : un changement fait hors connexion touche une chose que quelqu'un d'autre a changée
 * entre-temps. Rien n'est écrasé : la feuille compare champ par champ, tu gardes ta version ou la sienne.
 * Elle s'ouvre d'elle-même au premier conflit ; « Plus tard » la ferme (le bandeau garde « à trancher »).
 */
const props = defineProps<{ open: boolean }>();
const emit = defineEmits<{ close: [] }>();
const offline = useOfflineStore();
const auth = useAuthStore();
const { members, load } = useMembers();
const projects = shallowRef<ProjectSummary[]>([]);
const resolved = ref('');

const op = computed<ConflictOp | null>(() => offline.conflicts.find(isConflictOp) ?? null);
const today = computed(() => auth.config?.today ?? todayInParis());
const rows = computed(() =>
  op.value
    ? conflictRows(op.value, { today: today.value, members: members.value ?? [], projects: projects.value })
    : [],
);
const heads = computed(() => (op.value ? conflictHeads(op.value) : null));

onMounted(() => {
  void load();
  api
    .projects()
    .then((list) => (projects.value = list))
    .catch(() => undefined);
});
watch(op, () => {
  resolved.value = '';
});

async function keep(choice: 'mine' | 'theirs'): Promise<void> {
  const current = op.value;
  if (!current) return;
  resolved.value = choice === 'mine' ? 'Gardé : ta version.' : 'Gardé : sa version.';
  await offline.resolve(current.id, choice);
  if (!offline.conflicts.some(isConflictOp)) setTimeout(() => emit('close'), 900);
}
</script>

<template>
  <template v-if="props.open && op && heads">
    <div class="sheet-dim" aria-hidden="true" @click="emit('close')"></div>
    <div
      class="conflict-sheet"
      role="dialog"
      aria-modal="true"
      :aria-label="conflictTitle(op)"
      data-testid="conflict-sheet"
    >
      <h4>{{ conflictTitle(op) }}</h4>
      <p>« {{ op.title }} » : vous avez changé la même chose.</p>
      <div class="diff">
        <div class="mine">
          <small>{{ heads.mine }}</small>
          <span v-for="row in rows" :key="`m-${row.label}`" data-testid="conflict-mine"
            ><b>{{ row.label }} :</b> {{ row.mine }}</span
          >
        </div>
        <div>
          <small>{{ heads.theirs }}</small>
          <span v-for="row in rows" :key="`t-${row.label}`" data-testid="conflict-theirs"
            ><b>{{ row.label }} :</b> {{ row.theirs }}</span
          >
        </div>
      </div>
      <div class="row-actions">
        <button class="btn sm primary" type="button" data-testid="conflict-keep-mine" @click="keep('mine')">
          Garder ta version
        </button>
        <button class="btn sm" type="button" data-testid="conflict-keep-theirs" @click="keep('theirs')">
          Garder la sienne
        </button>
        <button class="btn sm ghost" type="button" @click="emit('close')">Plus tard</button>
      </div>
      <p v-if="resolved" class="resolved" role="status">{{ resolved }}</p>
    </div>
  </template>
</template>
